/**
 * Quiz-specific database operations
 */

/**
 * Save quizzes from offline data
 * @param {EduQuizDB} db - Database instance
 * @param {Array} resources - Resources array
 * @param {string} QUIZ - Resource type constant
 */
export async function saveQuizzes(db, resources, QUIZ) {
    const quizzesData = [];
    const questionsData = [];
    const questionOptionsData = [];

    for (const resource of resources) {
        if (resource.type === QUIZ) {
            // Save quiz
            quizzesData.push({
                serverResourceId: resource.id,
                name: resource.name,
                description: resource.description || null,
            });

            // Save questions
            if (resource.questions && resource.questions.length > 0) {
                for (const question of resource.questions) {
                    questionsData.push({
                        serverId: question.id,
                        quizServerResourceId: resource.id,
                        questionText: question.question_text,
                        questionType: question.question_type,
                        explanation: question.explanation || null,
                        order: question.order,
                        questionImageUuids: question.question_images?.map(img => img.uuid) || [],
                    });

                    // Save question options
                    if (question.options && question.options.length > 0) {
                        for (const option of question.options) {
                            questionOptionsData.push({
                                serverId: option.id,
                                questionServerId: question.id,
                                optionText: option.option_text,
                                optionTextMatch: option.option_text_match || null,
                                isCorrect: option.is_correct,
                                order: option.order,
                                hotspotZone: option.hotspot_zone || null,
                            });
                        }
                    }
                }
            }
        }
    }

    if (quizzesData.length > 0) {
        console.log("Saving quizzes:", quizzesData);
        await db.quizzes.bulkAdd(quizzesData);
    }

    if (questionsData.length > 0) {
        console.log("Saving questions:", questionsData);
        await db.quizQuestions.bulkAdd(questionsData);
    }

    if (questionOptionsData.length > 0) {
        console.log("Saving question options:", questionOptionsData);
        await db.quizQuestionOptions.bulkAdd(questionOptionsData);
    }
}

/**
 * Get a quiz by server resource ID
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getQuiz(db, serverResourceId) {
    try {
        return await db.quizzes
            .where("serverResourceId")
            .equals(serverResourceId)
            .first();
    } catch (error) {
        console.error("Failed to get quiz:", error);
        throw error;
    }
}

/**
 * Get questions for a specific quiz
 * @param {EduQuizDB} db - Database instance
 * @param {number} quizId - Quiz ID
 */
export async function getQuestionsByQuiz(db, quizId) {
    try {
        const questions = await db.quizQuestions
            .where("quizServerResourceId")
            .equals(quizId)
            .toArray();

        // Sort by order
        return questions.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
        console.error("Failed to get questions for quiz:", error);
        throw error;
    }
}

/**
 * Get options for a specific question
 * @param {EduQuizDB} db - Database instance
 * @param {number} questionServerId - Question server ID
 */
export async function getQuestionOptions(db, questionServerId) {
    try {
        const options = await db.quizQuestionOptions
            .where("questionServerId")
            .equals(questionServerId)
            .toArray();

        // Sort by order
        return options.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
        console.error("Failed to get question options:", error);
        throw error;
    }
}

/**
 * Get a quiz with its questions and options
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getQuizWithQuestions(db, serverResourceId) {
    try {
        const quiz = await getQuiz(db, serverResourceId);
        if (!quiz) {
            return null;
        }

        const questions = await getQuestionsByQuiz(db, serverResourceId);

        // Load options for each question
        const questionsWithOptions = await Promise.all(
            questions.map(async (question) => {
                const options = await getQuestionOptions(db, question.serverId);

                let questionImages = [];
                if (question.questionImageUuids?.length > 0) {
                    const assets = await db.getAssets(question.questionImageUuids);
                    questionImages = assets.map(img => ({
                        uuid: img.uuid,
                        url: img.url,
                        urlCachePWA: img.urlCachePWA,
                        extension: img.extension,
                    }));
                }

                return {
                    ...question,
                    options,
                    questionImages,
                };
            })
        );

        return {
            ...quiz,
            questions: questionsWithOptions,
        };
    } catch (error) {
        console.error("Failed to get quiz with questions:", error);
        throw error;
    }
}
